import { useCallback, useMemo } from 'react';

/**
 * Table state as query params, so a filtered view is shareable and survives a
 * refresh. Both apps run Refine with `syncWithLocation: false`; this replaces
 * it, and avoids Refine owning params for tables it does not render.
 *
 * Router-agnostic BY CONSTRUCTION: the caller passes `useSearchParams()`'s
 * values in. packages/ui must not import react-router (see
 * docs/feature-authoring.md).
 *
 * Encoding:
 *   ?<p>sort=name&<p>order=asc&<p>f.role=admin,owner&<p>q=acme&<p>page=2&<p>size=25
 * where <p> is `${prefix}.` when set. Params at their default are omitted;
 * unrecognized params are preserved untouched.
 */

export interface TableState {
  sort?: { field: string; order: 'ascend' | 'descend' };
  filters?: Record<string, string[]>;
  search?: string;
  page?: number;
  pageSize?: number;
}

const FILTER_PREFIX = 'f.';

/**
 * The page-size default, shared with `DataTable` (which imports this rather
 * than redeclaring `25`) so the two modules cannot drift: `encodeTableState`
 * needs it to know which value is safe to omit from the URL, and
 * `isSearchOnlyChange` needs the same value to treat an omitted `size` param
 * and an explicit `size=25` as equal.
 */
export const DEFAULT_PAGE_SIZE = 25;

/** Namespaces a param name under `opts.prefix`, so two tables on one route don't collide. */
function key(prefix: string | undefined, name: string): string {
  return prefix ? `${prefix}.${name}` : name;
}

/**
 * `decodeURIComponent`, tolerant of a hand-edited or truncated URL. A lone
 * `%` (e.g. `?f.name=%`) is not valid `application/x-www-form-urlencoded`
 * decoded by `URLSearchParams`, but `decodeURIComponent` still rejects it as
 * a malformed percent-escape and throws a `URIError`. That throw happens
 * inside `useUrlTableState`'s render-time `useMemo`, with no error boundary
 * above it, so a stray `%` in the address bar would otherwise take out the
 * whole screen. Falling back to the raw segment keeps the state usable —
 * the field just decodes to whatever the user typed.
 */
function decodeSegment(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Reads `TableState` back out of query params. A `sort` with no `order`
 * defaults to `ascend`; an `order` with no `sort` is ignored entirely (there
 * is nothing to apply it to). A non-numeric or non-positive `page`/`size` is
 * ignored rather than trusted, since the URL is user-editable.
 */
export function decodeTableState(params: URLSearchParams, prefix?: string): TableState {
  const state: TableState = {};

  const sortField = params.get(key(prefix, 'sort'));
  if (sortField) {
    state.sort = {
      field: sortField,
      order: params.get(key(prefix, 'order')) === 'desc' ? 'descend' : 'ascend',
    };
  }

  const filters: Record<string, string[]> = {};
  const fPrefix = key(prefix, FILTER_PREFIX);
  for (const [k, v] of params.entries()) {
    if (!k.startsWith(fPrefix) || v === '') continue;
    filters[k.slice(fPrefix.length)] = v.split(',').map((s) => decodeSegment(s));
  }
  if (Object.keys(filters).length > 0) state.filters = filters;

  const q = params.get(key(prefix, 'q'));
  if (q) state.search = q;

  const page = Number(params.get(key(prefix, 'page')));
  if (Number.isInteger(page) && page > 0) state.page = page;

  const size = Number(params.get(key(prefix, 'size')));
  if (Number.isInteger(size) && size > 0) state.pageSize = size;

  return state;
}

/**
 * Writes `TableState` into query params, starting from `base`. Always clears
 * this table's own params first (scoped by `prefix`) so a value that is now
 * absent from `state` is genuinely removed rather than left stale; params
 * belonging to other tables or the route itself (e.g. `tab=overview`) are
 * left untouched. Values equal to their default (`page: 1`, `pageSize:
 * DEFAULT_PAGE_SIZE`, empty search, empty filter arrays) are omitted so the
 * URL stays minimal — otherwise a plain sort click (which always writes an
 * explicit `pageSize`, see `DataTable.handleChange`) would permanently append
 * `&size=25` to every table URL, making a shared link look stateful when it
 * is not.
 */
export function encodeTableState(
  base: URLSearchParams,
  state: TableState,
  prefix?: string,
): URLSearchParams {
  const out = new URLSearchParams(base);

  // Clear this table's params, leaving everyone else's alone.
  const fPrefix = key(prefix, FILTER_PREFIX);
  for (const k of Array.from(out.keys())) {
    if (
      k === key(prefix, 'sort') ||
      k === key(prefix, 'order') ||
      k === key(prefix, 'q') ||
      k === key(prefix, 'page') ||
      k === key(prefix, 'size') ||
      k.startsWith(fPrefix)
    ) {
      out.delete(k);
    }
  }

  if (state.sort) {
    out.set(key(prefix, 'sort'), state.sort.field);
    out.set(key(prefix, 'order'), state.sort.order === 'descend' ? 'desc' : 'asc');
  }
  for (const [field, values] of Object.entries(state.filters ?? {})) {
    if (!values || values.length === 0) continue;
    out.set(`${fPrefix}${field}`, values.map((v) => encodeURIComponent(v)).join(','));
  }
  if (state.search) out.set(key(prefix, 'q'), state.search);
  if (state.page && state.page > 1) out.set(key(prefix, 'page'), String(state.page));
  if (state.pageSize && state.pageSize !== DEFAULT_PAGE_SIZE) {
    out.set(key(prefix, 'size'), String(state.pageSize));
  }

  return out;
}

/** Structural (not reference) equality for `TableState['sort']`. */
function sortEqual(a: TableState['sort'], b: TableState['sort']): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.field === b.field && a.order === b.order;
}

/** Structural (not reference) equality for `TableState['filters']`. */
function filtersEqual(a?: Record<string, string[]>, b?: Record<string, string[]>): boolean {
  const aKeys = Object.keys(a ?? {});
  const bKeys = Object.keys(b ?? {});
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => {
    const av = a?.[k] ?? [];
    const bv = b?.[k] ?? [];
    return av.length === bv.length && av.every((v, i) => v === bv[i]);
  });
}

export interface UseUrlTableStateOptions {
  /** Namespaces this table's params so two tables can share one route. */
  prefix?: string;
  /**
   * Seeds only the fields the URL does not already carry — a user who
   * explicitly cleared a sort must not be fought by the default on every
   * render. Expected to be referentially stable (e.g. a module-level
   * constant or memoized by the caller): it is intentionally left out of
   * this hook's memo dependencies, so a defaultState that changes identity
   * on every render without the URL changing will not be picked up until
   * the next URL change.
   */
  defaultState?: Partial<TableState>;
}

/**
 * Router-agnostic table state synced to the URL. Sort, filter and page (and
 * page-size) changes push a history entry; a change that touches only
 * `search` uses `replace: true` so a debounced keystroke-by-keystroke write
 * does not make the back button step through every character typed.
 *
 * "Search-only" is decided by comparing every other field by value (not
 * reference), so a caller that rebuilds its `sort`/`filters` objects on every
 * change (rather than reusing the previous reference) is still classified
 * correctly, and a single call that bundles a search edit together with a
 * sort/filter/page change still pushes.
 *
 * `setState` always writes via a `setSearchParams` *updater function*, never
 * a plain `URLSearchParams` snapshot. The base for the merge is the `prev`
 * argument the router hands the updater at write time, never a `searchParams`
 * closed over when `setState` was created. This matters because two
 * state-changing calls can land in the same tick (e.g. antd's `onChange` and
 * a debounced search commit both firing before React re-renders with a fresh
 * `searchParams`): with a closed-over base, the second call would diff
 * against the same stale snapshot as the first and clobber it; with an
 * updater, the router applies each write against the result of the previous
 * one, so both changes merge.
 */
export function useUrlTableState(
  searchParams: URLSearchParams,
  setSearchParams: (
    next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
    opts?: { replace?: boolean },
  ) => void,
  opts?: UseUrlTableStateOptions,
): [TableState, (next: TableState) => void] {
  const prefix = opts?.prefix;

  const state = useMemo(() => {
    const decoded = decodeTableState(searchParams, prefix);
    // defaultState seeds only what the URL does not carry, so a user who
    // explicitly cleared a sort is not fought by the default on every render.
    return { ...opts?.defaultState, ...decoded };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), prefix]);

  const setState = useCallback(
    (next: TableState) => {
      // Compared against NORMALISED defaults, not raw fields: `state.page`
      // (decoded from the URL) is `undefined` whenever the table is on page 1
      // — `encodeTableState` omits `page` at its default — while callers such
      // as `DataTable.commitSearch` always write an explicit `page: 1` on a
      // search commit. Comparing `next.page === state.page` directly (`1 ===
      // undefined`) was therefore false on every session's first search, and
      // `isSearchOnlyChange` never fired: every debounced keystroke pushed a
      // history entry instead of replacing it. `pageSize` gets the same
      // treatment against `DEFAULT_PAGE_SIZE` for the same reason.
      const isSearchOnlyChange =
        next.search !== state.search &&
        sortEqual(next.sort, state.sort) &&
        filtersEqual(next.filters, state.filters) &&
        (next.page ?? 1) === (state.page ?? 1) &&
        (next.pageSize ?? DEFAULT_PAGE_SIZE) === (state.pageSize ?? DEFAULT_PAGE_SIZE);
      setSearchParams((prev) => encodeTableState(prev, next, prefix), {
        replace: isSearchOnlyChange,
      });
    },
    [setSearchParams, prefix, state],
  );

  return [state, setState];
}
